import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  SubscriptionRequest,
  SubscriptionRequestModel,
  SubscriptionRequestDocumentData,
  SubscriptionType,
  RequestStatus
} from '../models/SubscriptionRequest';

// Mirrors iOS SubscriptionManager
export class SubscriptionService {
  static async submitRequest(
    userId: string,
    fullName: string,
    phoneNumber: string,
    email: string,
    subscriptionType: SubscriptionType
  ): Promise<void> {
    await addDoc(collection(db, 'subscription_requests'), {
      userId,
      userFullName: fullName,
      userPhoneNumber: phoneNumber,
      userEmail: email,
      subscriptionType,
      requestedAt: serverTimestamp(),
      status: RequestStatus.PENDING
    });
  }

  static async getUserRequests(userId: string): Promise<SubscriptionRequest[]> {
    const q = query(
      collection(db, 'subscription_requests'),
      where('userId', '==', userId),
      orderBy('requestedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d =>
      SubscriptionRequestModel.fromFirestore(d.id, d.data() as SubscriptionRequestDocumentData)
    );
  }

  // MARK: - Admin

  static async getPendingRequests(): Promise<SubscriptionRequest[]> {
    const q = query(
      collection(db, 'subscription_requests'),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d =>
      SubscriptionRequestModel.fromFirestore(d.id, d.data() as SubscriptionRequestDocumentData)
    );
  }

  // Mirrors iOS approveRequest — writes subscription fields to user docs.
  // Only succeeds when called by an admin (Firestore rules enforce this).
  static async approveRequest(request: SubscriptionRequest, adminId: string): Promise<void> {
    const endDate = new Date();
    if (request.subscriptionType === SubscriptionType.MONTHLY) {
      endDate.setMonth(endDate.getMonth() + 1);
    } else {
      endDate.setFullYear(endDate.getFullYear() + 1);
    }

    await updateDoc(doc(db, 'subscription_requests', request.id), {
      status: 'approved',
      processedAt: serverTimestamp(),
      processedBy: adminId
    });

    const subscriptionData = {
      subscriptionStatus: 'active',
      subscriptionType: request.subscriptionType,
      subscriptionEndDate: Timestamp.fromDate(endDate),
      isTrialActive: false
    };

    await updateDoc(doc(db, 'all_users', request.userId), subscriptionData);

    const userDoc = doc(db, 'users', request.userId);
    const userSnap = await getDocs(query(collection(db, 'users'), where('__name__', '==', request.userId)));
    if (!userSnap.empty) {
      await updateDoc(userDoc, subscriptionData);
    }
  }

  static async rejectRequest(requestId: string, adminId: string, notes?: string): Promise<void> {
    await updateDoc(doc(db, 'subscription_requests', requestId), {
      status: 'rejected',
      processedAt: serverTimestamp(),
      processedBy: adminId,
      ...(notes ? { notes } : {})
    });
  }
}
